import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import { CcRequirementType, CcRequirementTypeCreate } from "server/models"
import { useInput, StateType } from "client/utils"
import {
    StoreType,
    CcRequirementTypeValidation,
    SaveCcRequirementType,
    closeModal,
    setCcRequirementTypeEdit,
    setCcRequirementTypeReloadList,
    setCcRequirementTypeReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    SelectDropdown,
    SelectDropdownItem,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

interface TypeEditProps {
    type: CcRequirementType
    editAccess: boolean
}

let isMounted: boolean

const Form = ({
    type,
    editAccess,
}: TypeEditProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(type.name)
    const [description, setDescription] = useInput(type.description)
    const [isDefault, setDefault] = useState(type.default)
    const [validation, setValidation] = useState<CcRequirementTypeValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newTypeData: CcRequirementTypeCreate = {
            accountId: type.accountId || userData.accountId,
            projectId: type.projectId || project.id,
            name,
            description,
            creator: type.id ? type.creator : userData.userId,
            default: isDefault,
        }

        SaveCcRequirementType(type.id, newTypeData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setCcRequirementTypeEdit({
                ...newTypeData,
                id: res.newTypeId || type.id,
            }))

            dispatch(setCcRequirementTypeReloadList(false))
            setTimeout(() => {
                dispatch(setCcRequirementTypeReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setCcRequirementTypeReloadEdit(false))
                setTimeout(() => {
                    dispatch(setCcRequirementTypeReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} readOnly={!editAccess} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} readOnly={!editAccess} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.default}</Label>
                        {
                            editAccess && (
                                <SelectDropdown title={isDefault ? lang.yes : lang.no}>
                                    <SelectDropdownItem onClick={() => setDefault(true)}>{lang.yes}</SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => setDefault(false)}>{lang.no}</SelectDropdownItem>
                                </SelectDropdown>
                            )
                        }
                        {
                            !editAccess && (
                                <Input type="text" value={isDefault ? lang.yes : lang.no} readOnly />
                            )
                        }
                        <Feedback>{validation && validation.default}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    {
                        editAccess && [
                            <Button key="save" variant="success" row onClick={() => onSave(1)}>
                                {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                            </Button>,
                            <Button key="apply" variant="success" row onClick={() => onSave(2)}>
                                {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                            </Button>,
                        ]
                    }
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
